import { User } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { DashboardCard } from "./DashboardCard";
import { Building, Calendar, DollarSign, PlusCircle, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from '@/lib/api';

interface OwnerDashboardProps {
  user: User;
}

export function OwnerDashboard({ user }: OwnerDashboardProps) {
  // Fetch manager properties
  const { data: properties = [], isLoading: propertiesLoading } = useQuery({
    queryKey: ['my-properties'],
    enabled: !!user && (user.role === 'manager' || user.role === 'owner' || user.role === 'admin'),
    queryFn: async () => {
      console.log("Fetching properties with auth, user:", user);
      const response = await apiRequest('GET', 'my-properties');
      return response;
    },
  });
  
  // Fetch bookings
  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['property-bookings/my'],
    enabled: !!user && (user.role === 'manager' || user.role === 'owner' || user.role === 'admin'),
    queryFn: async () => {
      console.log("Fetching bookings for user:", user);
      try {
        const response = await apiRequest('GET', 'property-bookings/my');
        
        console.log("Bookings data received:", response);
        return response || [];
      } catch (error) {
        console.error("Error fetching bookings:", error);
        return [];
      }
    },
  });
  
  // Fetch earnings data
  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['manager/earnings'],
    enabled: !!user && (user.role === 'manager' || user.role === 'owner' || user.role === 'admin'),
    queryFn: async () => {
      console.log("Fetching earnings for user:", user);
      try {
        const data = await apiRequest('GET', 'manager/earnings');
        console.log("Earnings data received:", data);
        return data;
      } catch (error) {
        console.error("Failed to fetch earnings:", error);
        return { totalEarnings: 0, pendingPayouts: 0, paidEarnings: 0 };
      }
    }
  });

  // Calculate dashboard data from real API data
  const activeProperties = properties?.filter(p => p.status === 'active').length;
  const totalProperties = properties?.length;
  
  // Filter to only show confirmed and upcoming bookings
  const activeBookings = bookings?.filter(b => b.status === 'confirmed' || b.status === 'pending').length;
  
  // Calculate total revenue (or use the API response if available)
  const totalRevenue = earnings?.totalEarnings || user.totalEarnings || 0;
  
  // Get 4 most recent bookings
  const recentBookings = bookings?.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }).slice(0, 4);
  
  // Get 4 most recent properties
  const recentProperties = properties?.slice(0, 4);

  // Functions to format data for display
  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isLoading = propertiesLoading || bookingsLoading || earningsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading dashboard data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold">Welcome, {user.fullName ? user.fullName.split(' ')[0] : user.username}</h2>
        <p className="text-muted-foreground">Here's an overview of your properties and bookings</p>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <DashboardCard
          title="Total Properties"
          value={totalProperties}
          icon={<Building className="h-6 w-6" />}
          subtitle="Properties you manage"
        />
        <DashboardCard
          title="Active Bookings"
          value={activeBookings}
          icon={<Calendar className="h-6 w-6" />}
          subtitle="Current and upcoming stays"
          iconBgClass="bg-blue-100"
          iconTextClass="text-blue-500"
        />
        <DashboardCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          icon={<DollarSign className="h-6 w-6" />}
          subtitle="Lifetime earnings"
          iconBgClass="bg-green-100"
          iconTextClass="text-green-500"
        />
      </div>

      {/* Recent Bookings */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Recent Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {recentBookings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">
                      {booking.propertyTitle || `Property #${booking.propertyId}`}
                    </TableCell>
                    <TableCell>{booking.guestName || "Guest"}</TableCell>
                    <TableCell>
                      {booking.checkIn && booking.checkOut ? 
                        `${formatDate(booking.checkIn)} - ${formatDate(booking.checkOut)}` :
                        "Dates not available"}
                    </TableCell>
                    <TableCell>{formatCurrency(booking.totalAmount / 100)}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          booking.status === "confirmed" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                          booking.status === "pending" ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100" :
                          booking.status === "completed" ? "bg-blue-100 text-blue-800 hover:bg-blue-100" :
                          "bg-red-100 text-red-800 hover:bg-red-100"
                        }
                      >
                        {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || "Unknown"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No bookings found.</p>
            </div>
          )}
          
          <div className="mt-4">
            <Button variant="outline" asChild>
              <Link href="/manager/bookings">View All Bookings</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Properties */}
      <Card className="mt-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Your Properties</CardTitle>
          <Button asChild size="sm">
            <Link href="/manager/properties/add">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Property
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentProperties.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Bedrooms</TableHead>
                  <TableHead>Bathrooms</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentProperties.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell className="font-medium">
                      <Link href={`/manager/edit-property/${property.id}`}>
                        {property.title}
                      </Link>
                    </TableCell>
                    <TableCell>{property.area}</TableCell>
                    <TableCell>{property.bedrooms}</TableCell>
                    <TableCell>{property.bathrooms}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          property.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                          property.status === "pending" ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100" :
                          "bg-red-100 text-red-800 hover:bg-red-100"
                        }
                      >
                        {property.status?.charAt(0).toUpperCase() + property.status?.slice(1) || "Unknown"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No properties found.</p>
              <Button className="mt-4" asChild>
                <Link href="/manager/properties/add">Add Your First Property</Link>
              </Button>
            </div>
          )}
          
          {recentProperties.length > 0 && (
            <div className="mt-4">
              <Button variant="outline" asChild>
                <Link href="/manager/properties">View All Properties</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}